import clsx from "clsx";
import type { PlatinumSeatView } from "../../api/guild";

export interface PlatinumClaimInfo {
  seat_index: number;
  count: number;
  claimants?: { user_id: number; display_name: string }[];
}

interface PlatinumSeatGridProps {
  seats: PlatinumSeatView[];
  claims?: PlatinumClaimInfo[];
  myClaim?: number | null;
  claimMode?: boolean;
  allowedSeats?: number[];
  actionLoading?: boolean;
  onClaim?: (seatIndex: number) => void;
  showHolders?: boolean;
}

const SEAT_LAYOUT: number[][] = [[0], [1, 2, 3], [4, 5, 6, 7, 8, 9]];

function seatByIndex(seats: PlatinumSeatView[], index: number): PlatinumSeatView | undefined {
  return seats.find((s) => s.seat_index === index);
}

function claimForSeat(claims: PlatinumClaimInfo[], index: number): PlatinumClaimInfo | undefined {
  return claims.find((c) => c.seat_index === index);
}

function SeatCell({
  seat,
  claim,
  mine,
  claimMode,
  allowed,
  actionLoading,
  onClaim,
  showHolders,
}: {
  seat?: PlatinumSeatView;
  claim?: PlatinumClaimInfo;
  mine: boolean;
  claimMode: boolean;
  allowed: boolean;
  actionLoading: boolean;
  onClaim?: (seatIndex: number) => void;
  showHolders: boolean;
}) {
  if (!seat) return null;
  const contested = (claim?.count ?? 0) > 1;
  const hasClaimants = (claim?.count ?? 0) > 0;

  const content = (
    <>
      <div className="text-[10px] text-gray-500">{seat.status_emoji}</div>
      <div className="text-[9px] text-violet-300 font-semibold truncate">{seat.title}</div>
      {claimMode ? (
        <>
          {hasClaimants && (
            <div className="text-[8px] text-amber-300">{claim!.count} registered</div>
          )}
          {claim?.claimants?.slice(0, 2).map((c) => (
            <div key={c.user_id} className="text-[8px] text-gray-400 truncate">
              {c.display_name}
            </div>
          ))}
        </>
      ) : showHolders ? (
        <div className="text-[9px] text-white truncate">{seat.display_name ?? "Empty"}</div>
      ) : null}
    </>
  );

  if (claimMode && onClaim) {
    return (
      <button
        type="button"
        disabled={actionLoading || !allowed}
        onClick={() => onClaim(seat.seat_index)}
        className={clsx(
          "rounded-lg border p-1.5 min-h-[52px] text-left transition-colors",
          !allowed && "opacity-40 cursor-not-allowed",
          allowed &&
            (mine
              ? "border-violet-400 bg-violet-900/50"
              : contested
                ? "border-amber-600/60 bg-amber-950/30"
                : hasClaimants
                  ? "border-kingdom-mid bg-kingdom-dark/80"
                  : "border-kingdom-mid bg-kingdom-dark/60 hover:border-violet-500"),
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={clsx(
        "rounded-lg border p-1.5 min-h-[52px]",
        mine ? "border-violet-400 bg-violet-900/30" : "border-kingdom-mid bg-kingdom-dark/80",
      )}
    >
      {content}
    </div>
  );
}

export function PlatinumSeatGrid({
  seats,
  claims = [],
  myClaim = null,
  claimMode = false,
  allowedSeats,
  actionLoading = false,
  onClaim,
  showHolders = true,
}: PlatinumSeatGridProps) {
  const allowAll = allowedSeats == null;
  return (
    <div className="space-y-2">
      {SEAT_LAYOUT.map((row, rowIdx) => (
        <div
          key={rowIdx}
          className={clsx(
            "grid gap-1.5",
            row.length === 1 && "grid-cols-1 max-w-[120px] mx-auto",
            row.length === 3 && "grid-cols-3",
            row.length === 6 && "grid-cols-3",
          )}
        >
          {row.map((seatIndex) => (
            <SeatCell
              key={seatIndex}
              seat={seatByIndex(seats, seatIndex)}
              claim={claimForSeat(claims, seatIndex)}
              mine={myClaim === seatIndex}
              claimMode={claimMode}
              allowed={allowAll || allowedSeats.includes(seatIndex)}
              actionLoading={actionLoading}
              onClaim={onClaim}
              showHolders={showHolders}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
